import { formatCurrency, formatDate } from '@if/core/utils'
import { useConfirmDialog } from '@if/mui'
import { useComputeCalendarAction } from '@src/api/loan/loan-api'
import { ProductRefundOperation } from '@src/types/loan/loan-operation'
import { ECalendarSimulationAction, SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Cancels one complete refund from its calendar row, once the agent confirms. A refund never
 * returns to Complete, so the confirmation says so. Null when the agent backs out.
 */
export const useCancelProductRefundOnCalendar = (
  signedSimulation: SignedCalendarSimulation,
  isLockedByOther: boolean,
) => {
  const { t } = useTranslation()
  const { loan } = signedSimulation.simulation.fullLoan
  const { mutateAsync: computeAction } = useComputeCalendarAction(loan.id, loan.financingCompanyId)
  // A confirm dialog resolves with a boolean only: the recomputed calendar comes back through here.
  const resultRef = useRef<SignedCalendarSimulation | null>(null)

  const cancelRefund = useCallback(
    (refund: ProductRefundOperation): Promise<SignedCalendarSimulation> =>
      computeAction({
        ...signedSimulation,
        action: { actionType: ECalendarSimulationAction.CancelProductRefund, refundId: refund.id },
      }),
    [computeAction, signedSimulation],
  )

  const confirm = useConfirmDialog<ProductRefundOperation>({
    title: t('loan.payment.productRefundDialog.cancelTitle'),
    message: ({ props: refund }) =>
      t('loan.payment.productRefundDialog.cancelMessage', {
        amount: formatCurrency(refund.amount),
        date: formatDate(refund.effectiveDate),
        products: refund.refundedProducts.map((line) => t(`enum.product.${line.productId}`)).join(', '),
      }),
    confirmLabel: t('loan.payment.productRefundDialog.cancelTitle'),
    mode: 'destructive',
    confirmDisabled: () => isLockedByOther,
    confirmTooltip: () => (isLockedByOther ? t('common.lockTooltip.lockedByOther') : null),
    onConfirm: async ({ props: refund }) => {
      resultRef.current = await cancelRefund(refund)
    },
  })

  return useCallback(
    async (refund: ProductRefundOperation): Promise<SignedCalendarSimulation | null> => {
      resultRef.current = null
      return (await confirm.open(refund)) ? resultRef.current : null
    },
    [confirm],
  )
}
